import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { Link, useNavigate } from "react-router-dom";
import { CiSearch } from "react-icons/ci";
import { GoPlus } from "react-icons/go";
import { RiDeleteBin4Line } from "react-icons/ri";
import { LiaEditSolid } from "react-icons/lia";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";

export const AdminManageExercise = () => {
  const { staff } = useStaffAuthContext();
  const navigate = useNavigate();

  const [exercises, setExercises] = useState([]);
  const [exerciseTypes, setExerciseTypes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("");

  const [selectedVideo, setSelectedVideo] = useState(null);

  // 🔹 Fetch exercises from backend
  const fetchExercises = async (page = 1, search = "", type = "") => {
    try {
      setIsLoading(true);
      setError("");

      console.log(
        "🔎 Fetching:",
        `${API_URL.GET_EXERCISE_LIBRARY_ADMIN}?page=1&limit=50&search=${search}&type=${selectedType}`,
      );

      const response = await fetch(
        `${API_URL.GET_EXERCISE_LIBRARY_ADMIN}?page=${page}&limit=50&search=${encodeURIComponent(
          search,
        )}&type=${encodeURIComponent(type)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        },
      );

      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Failed to fetch exercises");

      setExercises(data.exercises);
      setCurrentPage(data.currentPage || 1);
      setTotalPages(data.totalPages || 1);

      // extract unique types
      extractUniqueTypes(data.exercises);
    } catch (err) {
      console.error("Error fetching exercises:", err.message);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const extractUniqueTypes = (exerciseList) => {
    const types = [
      ...new Set(exerciseList.map((exercise) => exercise.type).filter(Boolean)),
    ];
    setExerciseTypes(types);
  };

  // 🔹 Delete Exercise
  const deleteExercise = async (id) => {
    try {
      setIsDeleting(true);
      const response = await fetch(
        `${API_URL.DELETE_EXERCISE_LIBRARY_ADMIN}/${id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${staff.token}` },
        },
      );

      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Failed to delete exercise");

      fetchExercises(currentPage, searchQuery, selectedType);
      alert("Exercise deleted successfully!");
    } catch (err) {
      alert(`Error deleting exercise: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this exercise?")) {
      deleteExercise(id);
    }
  };

  // 🔹 Search + Filter
  const handleSearch = (query) => {
    setSearchQuery(query);
    fetchExercises(1, query, selectedType);
  };

  const handleTypeFilter = (type) => {
    setSelectedType(type);
    fetchExercises(1, searchQuery, type);
  };

  // 🔹 Pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchExercises(newPage, searchQuery, selectedType);
    }
  };

  // 🔹 Video Handling
  const extractYouTubeId = (videoUrl) => {
    if (!videoUrl) return null;
    const match = videoUrl.match(
      /(?:youtube\.com\/(?:shorts\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/,
    );
    return match ? match[1] : null;
  };

  const extractYouTubeThumbnail = (videoUrl) => {
    const id = extractYouTubeId(videoUrl);
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
  };

  const getEmbedUrl = (videoUrl) => {
    const id = extractYouTubeId(videoUrl);
    return id ? `https://www.youtube.com/embed/${id}` : null;
  };

  const openVideoModal = (videoUrl) => setSelectedVideo(videoUrl);
  const closeVideoModal = () => setSelectedVideo(null);

  // 🔹 Initial Load
  useEffect(() => {
    fetchExercises(currentPage, searchQuery, selectedType);
  }, []);

  return (
    <div className="flex md:h-screen">
      <StaffSideMenu />

      <div className="max-md:w-full md:flex-1 md:flex md:flex-col md:overflow-hidden md:overflow-y-auto  bg-bgBase pb-2">
        {/* <div className="max-md:w-full md:flex-1 bg-bgBase md:overflow-y-auto md:overflow-hidden pb-5"> */}
        {/* Top Action */}
        <div className="bg-white pt-5 pb-3 md:py-6 px-5 md:px-10 flex max-lg:flex-col justify-between lg:items-center gap-5">
          <h1 className="font-inter font-medium text-dark text-2xl md:text-3xl !leading-none max-md:pl-14">
            Manage Exercise Library
          </h1>

          {/* create btn / search */}
          <div className="flex  max-md:flex-col md:items-center gap-3">
            <Link
              to="/administrator/exercise-library/create"
              className="bg-dark w-fit rounded-10 py-2.5 px-4 flex items-center gap-2 text-white font-inter font-medium whitespace-nowrap hover:bg-black"
            >
              <GoPlus color="#fff" size={16} className="-mb-px" />
              Create Exercise
            </Link>
            {/* search */}
            <div className="relative w-full min-w-64 max-w-64">
              <input
                type="text"
                placeholder="Search by title or tags"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full p-3 border border-black/10 rounded-lg font-inter font-normal text-sm leading-none pl-10"
              />{" "}
              <span className="absolute top-1/2 -translate-y-1/2 left-3">
                <CiSearch color="#666666" size={20} />
              </span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="my-4 px-5 md:px-10">
          <div className="w-full max-w-52 md:ml-auto">
            <select
              value={selectedType}
              onChange={(e) => handleTypeFilter(e.target.value)}
              className="w-full outline-none bg-white border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight capitalize"
            >
              <option value="">All Types</option>
              {exerciseTypes.map((type, index) => (
                <option key={index} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="w-full px-5 md:px-10">
          <div className="overflow-auto show-scrollbar max-h-[600px]">
            <div className="rounded-10 border border-tblBorder w-full">
              <table className="table-auto table-radius w-full min-w-[540px]">
                <thead className="sticky top-0 bg-tblHead border-b border-tblBorder">
                  <tr className="bg-tblHead text-slate100 font-inter !font-medium text-xs md:text-sm uppercase">
                    <th className="py-1.5 md:py-3 px-3 md:px-6 text-left">
                      Video
                    </th>
                    <th className="py-1.5 md:py-3 px-3 md:px-6 text-left">
                      Title
                    </th>
                    <th className="py-1.5 md:py-3 px-3 md:px-6 text-left">
                      Type
                    </th>
                    <th className="py-1.5 md:py-3 px-3 md:px-6 text-left">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="text-center font-inter font-normal text-dark text-xs md:text-sm py-4"
                      >
                        Loading data...
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="text-center font-inter font-normal text-xs md:text-sm py-4 text-red-600"
                      >
                        {error}
                      </td>
                    </tr>
                  ) : exercises.length === 0 ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="text-center font-inter font-normal text-dark text-xs md:text-sm py-4"
                      >
                        No records found
                      </td>
                    </tr>
                  ) : (
                    exercises.map((exercise) => {
                      const thumbnail = extractYouTubeThumbnail(
                        exercise.video_url,
                      );
                      return (
                        <tr
                          key={exercise._id}
                          className="bg-white font-inter font-medium text-dark text-xs md:text-sm"
                        >
                          <td className="px-3 md:px-6 py-2 md:py-4">
                            {thumbnail ? (
                              <img
                                src={thumbnail}
                                alt="YouTube Thumbnail"
                                className="w-12 h-12 rounded-lg border border-tblBorder cursor-pointer"
                                onClick={() =>
                                  openVideoModal(exercise.video_url)
                                }
                              />
                            ) : (
                              <span>No video</span>
                            )}
                          </td>
                          <td className="px-3 md:px-6 py-2 md:py-4">
                            {exercise.title}
                          </td>
                          <td className="px-3 md:px-6 py-2 md:py-4">
                            {exercise.type}
                          </td>
                          <td className="px-3 md:px-6 py-2 md:py-4">
                            <div className="flex gap-2">
                              <Link
                                to={`/administrator/exercise-library/edit/${exercise._id}`}
                                className="p-2 rounded-md hover:bg-slate-100"
                              >
                                <LiaEditSolid color="#666666" size={20} />
                              </Link>
                              <button
                                onClick={() => handleDelete(exercise._id)}
                                className="p-2 rounded-md hover:bg-slate-100"
                              >
                                <RiDeleteBin4Line color="#666666" size={20} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* Pagination */}
        <div className="flex max-sm:flex-col justify-between items-center mt-auto pt-2 px-5 md:px-10">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 min-w-28 flex justify-center items-center gap-1 rounded-lg text-[#99A1AF] font-inter font-normal text-sm ${
              currentPage === 1 ? "cursor-not-allowed" : "text-black"
            }`}
          >
            <MdKeyboardArrowLeft size={20} /> Previous
          </button>

          <span className="text-dark font-inter font-normal text-sm">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 min-w-28 flex justify-center items-center gap-1 rounded-lg text-[#99A1AF] font-inter font-normal text-sm ${
              currentPage === totalPages ? "cursor-not-allowed" : "text-black"
            }`}
          >
            Next <MdKeyboardArrowRight size={20} />
          </button>
        </div>

        {/* Video Modal */}
        {selectedVideo && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-4 rounded shadow-lg relative">
              <button
                className="absolute top-2 right-2 text-red-500"
                onClick={closeVideoModal}
              >
                ×
              </button>
              <iframe
                width="560"
                height="315"
                src={getEmbedUrl(selectedVideo)}
                title="YouTube Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
